import { useCallback, useEffect, useRef } from 'react';
import { loadTimedExposure } from './timedExposure';

// Video cues are authoritative. The server history restores a late joiner and
// catches cues skipped when the player seeks or reconnects.
export function useTimedExposure<T>(liveSeq: number | undefined, onExposure: (item: T) => void) {
  const onExposureRef = useRef(onExposure);
  onExposureRef.current = onExposure;
  const lastAppliedAt = useRef(-Infinity);
  const latencyRef = useRef<number | (() => number)>(0);
  const playbackTimeRef = useRef<(() => number) | undefined>(undefined);
  const playingRef = useRef(false);
  const requestRef = useRef(0);
  const pendingRequestRef = useRef<number | null>(null);
  const generationRef = useRef(0);

  const apply = useCallback((item: T, publishedAt?: number) => {
    if (Number.isFinite(publishedAt)) {
      if (publishedAt! <= lastAppliedAt.current) return;
      lastAppliedAt.current = publishedAt!;
    }
    onExposureRef.current(item);
  }, []);

  const sync = useCallback(() => {
    if (!liveSeq || !playingRef.current || pendingRequestRef.current !== null) return;
    let latency = NaN;
    let playbackTime: number | undefined;
    try { latency = typeof latencyRef.current === 'function' ? latencyRef.current() : latencyRef.current; } catch { /* Try the playback clock. */ }
    try { playbackTime = playbackTimeRef.current?.(); } catch { /* Use the measured latency. */ }
    const request = ++requestRef.current;
    pendingRequestRef.current = request;
    const generation = generationRef.current;
    void loadTimedExposure<T>(liveSeq, latency, playbackTime).then(({ current }) => {
      if (request !== requestRef.current || generation !== generationRef.current) return;
      if (current) apply(current.item, current.publishedAt);
      // Future changes must arrive with their video frame. A wall-clock timeout
      // can run ahead of a paused, buffering, or backgrounded player.
    }).catch(() => undefined).finally(() => {
      if (pendingRequestRef.current === request) pendingRequestRef.current = null;
    });
  }, [liveSeq, apply]);

  useEffect(() => {
    lastAppliedAt.current = -Infinity;
    // Playback can begin while the page still reports "예약". Keep its measured
    // latency when the live status switches to "방송중" without remounting the player.
    sync();
    const interval = window.setInterval(sync, 3000);
    const resume = () => { if (!document.hidden) sync(); };
    window.addEventListener('online', resume);
    document.addEventListener('visibilitychange', resume);
    return () => {
      generationRef.current += 1;
      requestRef.current += 1;
      pendingRequestRef.current = null;
      window.clearInterval(interval);
      window.removeEventListener('online', resume);
      document.removeEventListener('visibilitychange', resume);
    };
  }, [sync]);

  const onPlaybackStarted = useCallback((latencySeconds: number | (() => number), playbackTime?: () => number) => {
    playingRef.current = true;
    latencyRef.current = latencySeconds;
    playbackTimeRef.current = playbackTime;
    sync();
  }, [sync]);

  const onPlaybackPaused = useCallback(() => {
    playingRef.current = false;
    requestRef.current += 1;
    pendingRequestRef.current = null;
  }, []);

  const onMetadata = useCallback((item: T, publishedAt?: number) => {
    if (Number.isFinite(publishedAt) && publishedAt! <= lastAppliedAt.current) return;
    // A response requested before this video cue cannot replace it, even for legacy cues without timestamps.
    requestRef.current += 1;
    apply(item, publishedAt);
  }, [apply]);

  return { onPlaybackStarted, onPlaybackPaused, onMetadata };
}
